'use client';

// import { CustomImage as Image } from '..';
import React, { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { BookCallTFComponentDiv, Container, useModal } from '..';
import { PLargeGradient } from '../blood-vision/styleConstants';
import gsap from 'gsap';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { analytics } from 'lib/analytics/analytics';
import { splitTextIntoSpans } from 'components/shared/SplitTextIntoSpans';
import styles from './ProductWizard.module.css';

export const ProductWizard = ({
  className,
  title,
  backgroundColor,
}: {
  className?: string;
  title: string;
  backgroundColor?: string;
}) => {
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);
  const mountedRef = useRef(true);

  const ModalClient = useModal();

  const animationInit = () => {
    if (
      !mountedRef.current ||
      !container.current ||
      !window ||
      hasAnimated.current
    )
      return;

    gsapContext.add(() => {
      const containerElement = container.current;
      if (!containerElement || !mountedRef.current) return;

      const tc1Spans = containerElement.querySelectorAll('.tc-1 > span');
      const tc2Element = containerElement.querySelector('.tc-2');
      const tc2Spans = containerElement.querySelectorAll('.tc-2 > span');
      if (!tc2Element || tc1Spans.length === 0 || tc2Spans.length === 0) return;

      const tl = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: containerElement,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.2,
          immediateRender: false,
        },
      });

      gsap.set([tc2Element, tc2Spans], {
        willChange: 'opacity, visibility',
      });

      tl.fromTo(
        tc1Spans,
        {
          autoAlpha: 0.2,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.2,
        },
        0.2,
      )
        .to(
          tc1Spans,
          {
            autoAlpha: 0,
            duration: 0.35,
          },
          '+=0.5',
        )
        .to(
          tc2Element,
          {
            autoAlpha: 1,
            duration: 0.01,
          },
          '+=0.5',
        )
        .fromTo(
          tc2Spans,
          {
            autoAlpha: 0.2,
          },
          {
            autoAlpha: 1,
            duration: 0.35,
            stagger: 0.2,
          },
        )
        .addPause(5.5)
        .eventCallback('onComplete', () => {
          gsap.set([tc1Spans, tc2Element, tc2Spans], {
            willChange: 'auto',
          });
        });

      add(tl, 'buy-product-wizard');
    });

    hasAnimated.current = true;
    intersectionObserver.current?.disconnect();
  };

  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  useIsomorphicLayoutEffect(() => {
    mountedRef.current = true;
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (!mountedRef.current || !entries[0]?.isIntersecting) return;
        animationInit();
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0,
      },
    );

    if (container.current) {
      intersectionObserver.current.observe(container.current);
    }

    return () => {
      mountedRef.current = false;
      intersectionObserver.current?.disconnect();
    };
  }, []);

  const showBookCallModal = () => {
    let url = 'website';
    if (window) {
      url = window.location.hostname + window.location.pathname;
    }
    analytics.track(`product wizard talk to expert - CLICK`);
    ModalClient.set(
      <BookCallTFComponentDiv
        trackingParams={{
          utm_source: url,
          utm_medium: 'product-wizard-section',
        }}
      />,
    );
    ModalClient.setCloseButtonTheme('light');
    ModalClient.show();
  };

  const t = useTranslations('common');

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={container}
      style={
        { ['--pw-bg']: backgroundColor || '#fff' } as React.CSSProperties
      }
    >
      <div className={styles.stickyWrapper}>
        <Container>
          <PLargeGradient
            className={`${styles.animTextContent} ${styles.tc1} tc-1`}
          >
            {splitTextIntoSpans(title)}
          </PLargeGradient>

          <PLargeGradient
            className={`${styles.animTextContent} ${styles.tc2} tc-2`}
          >
            {splitTextIntoSpans(t('ring.productWizard.contentB_A'))}
            <br className={styles.desktopBr} />{' '}
            {splitTextIntoSpans(t('ring.productWizard.contentB_B'))}
            <span
              style={{
                fontSize: '24px',
                fontWeight: '400',
                lineHeight: 'normal',
                letterSpacing: '-0.96px',
                display: 'block',
                marginTop: '1.6rem',
              }}
            >
              {/* Whether you&apos;re curious, skeptical, or just want to know how{' '} */}
              {t('ring.productWizard.contentC.textA')} <br />
              {/* it would work for you, we&apos;re here to help. */}
              {t('ring.productWizard.contentC.textB')}
            </span>
            <span
              style={{
                fontSize: '24px',
                fontWeight: '400',
                lineHeight: 'normal',
                letterSpacing: '-0.96px',
                display: 'block',
                marginTop: '4rem',
              }}
            >
              <button onClick={showBookCallModal}>
                {/* Talk to an expert */}
                {t('ring.productWizard.contentC.btnText')}
              </button>
            </span>
          </PLargeGradient>
        </Container>
      </div>
    </section>
  );
};
